// Errors that happen outside a React render: event handlers, promises, a
// chunk that fails to load after a deploy (#2997).
//
// The error boundaries (AppErrorBoundary, RouteErrorBoundary) only see render
// crashes. Everything else used to surface through session replay's console
// capture, which #2511 switched off for privacy, so since 2026-09-29 the
// Desk's frontend_errors section has had nothing to read.
//
// What leaves the browser: the error's NAME (TypeError, ChunkLoadError), the
// chunk it was thrown from with its content hash cut off (CollegePage, not a
// URL), the route path, and whether it looks like a stale build. Never the
// message: messages quote values ("Unexpected token 'J', \"Jane Doe\"...")
// and are unbounded cardinality (the same rule as AppErrorBoundary, audit D1).

import { track, EVENTS } from './analytics';
import { isChunkLoadError } from '../utils/lazyWithRetry';

// One page life reports at most this many, and each distinct (kind, name,
// chunk) once: a render loop throwing every frame must not flood PostHog.
export const MAX_ERRORS_PER_PAGE = 5;

let installed = false;
let sent = 0;
const seen = new Set();

/** 'CollegePage-4f3a9c1e.js' (or a full URL to it) -> 'CollegePage'.
 * Anything not served from this origin is 'third_party' (an extension, the
 * Google sign-in script); no source at all is 'unknown'. */
export function chunkOf(filename, origin = (typeof window !== 'undefined' ? window.location.origin : '')) {
    if (!filename || typeof filename !== 'string') return 'unknown';
    let url;
    try { url = new URL(filename, origin || undefined); } catch { return 'unknown'; }
    if (origin && url.origin !== origin) return 'third_party';
    const base = url.pathname.split('/').pop() || '';
    const name = base.replace(/\.(m?js|jsx)$/i, '').replace(/[-.][A-Za-z0-9_]{6,}$/, '');
    return /^[A-Za-z0-9_]{1,40}$/.test(name) ? name : 'unknown';
}

function errorKind(err) {
    const name = err && typeof err === 'object' ? err.name : null;
    return /^[A-Za-z][A-Za-z0-9_]{0,39}$/.test(String(name || '')) ? String(name) : 'Error';
}

/** The event props for one error, or null when it must not be sent. Pure. */
export function errorProps(kind, err, filename, path) {
    const error_kind = errorKind(err);
    const chunk = chunkOf(filename || (err && err.fileName) || '');
    return {
        kind,
        error_kind,
        chunk,
        // A guest-review link's grant id is a bearer-ish token: report the
        // route, not the id.
        path: String(path || '/').split(/[?#]/)[0].replace(/^\/review\/[^/]+/, '/review/:grantId').slice(0, 120),
        stale_build: !!(err && isChunkLoadError(err)),
    };
}

export function reportError(kind, err, filename) {
    try {
        if (sent >= MAX_ERRORS_PER_PAGE) return false;
        const props = errorProps(kind, err, filename, window.location?.pathname);
        const key = `${props.kind}|${props.error_kind}|${props.chunk}`;
        if (seen.has(key)) return false;
        seen.add(key);
        sent += 1;
        track(EVENTS.JS_ERROR, props);
        return true;
    } catch {
        return false;   // error reporting must never throw an error of its own
    }
}

/** Install the window listeners once (StrictMode-safe). */
export function installErrorCapture() {
    if (installed || typeof window === 'undefined') return;
    installed = true;
    window.addEventListener('error', (e) => {
        // A resource that failed to load (an <img>) fires 'error' with no
        // `error` object; that is not a script failure.
        if (!e || !e.error) return;
        reportError('error', e.error, e.filename);
    });
    window.addEventListener('unhandledrejection', (e) => {
        reportError('rejection', e?.reason, null);
    });
}

export function _resetErrorCapture() { installed = false; sent = 0; seen.clear(); }
